<template>
  <v-app id="app_create_dealers">
    <v-container fluid class="pa-8">
      <v-row align="center">
        <v-col cols="7">
          <p class="text-h5 font-weight-bold mb-0">CREATE DEALERS</p>
          <p class="text-subtitle-2 text-grey">Create or bulk upload dealer data set according to your requirements.</p>
        </v-col>
        <v-col cols="5" class="text-right">
          <v-btn 
            color="#800080" 
            size="x-large" 
            variant="text" 
            @click="dealerBulkUploadDialog = true"
          >
            <v-icon>mdi-file-upload-outline</v-icon> DEALERS BULK UPLOAD
          </v-btn>
          <v-divider :thickness="3" class="divider-vertical" vertical></v-divider>
          <v-btn class="gray-text" size="x-large" variant="text" @click="backPageDealer()">
            <v-icon size="x-large">mdi-menu-left</v-icon>&nbsp;BACK
          </v-btn>
        </v-col>
      </v-row>

      <v-card class="mt-6" elevation="0" border rounded="lg">
        <v-card-title class="pa-4 text-subtitle-2 font-weight-bold text-uppercase text-grey-darken-1">
          Create New Dealer
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <v-form ref="form">
            <v-row>
              <!-- Left Column -->
              <v-col cols="12" md="6">
                <v-text-field 
                  v-model="dealer.dealer_name" 
                  label="Dealer Name *"
                  :placeholder="focused.dealer_name ? 'Type dealer name (letters and spaces only)' : ''"
                  variant="outlined" 
                  density="comfortable"
                  :rules="dealerNameRules"
                  @focus="focused.dealer_name = true"
                  @blur="focused.dealer_name = false"
                  @input="validateDealerName"
                ></v-text-field>

                <v-text-field 
                  v-model="dealer.company_name" 
                  label="Company / Institute Name *"
                  :placeholder="focused.company_name ? 'Type company or institute name' : ''"
                  variant="outlined" 
                  density="comfortable"
                  class="mt-4"
                  :rules="companyNameRules"
                  @focus="focused.company_name = true"
                  @blur="focused.company_name = false"
                ></v-text-field>
              </v-col>

              <!-- Right Column -->
              <v-col cols="12" md="6">
                <v-text-field 
                  v-model="dealer.dealer_code" 
                  label="Dealer Code *"
                  :placeholder="focused.dealer_code ? 'Type dealer code (e.g., DLR005)' : ''"
                  variant="outlined" 
                  density="comfortable"
                  :rules="dealerCodeRules"
                  @focus="focused.dealer_code = true"
                  @blur="focused.dealer_code = false"
                ></v-text-field>

                <!-- Phone Numbers Section -->
                <div class="mt-4">
                  <!-- Primary Phone Number -->
                  <div class="mb-3">
                    <v-row align="start">
                      <v-col cols="12" sm="4" md="4" lg="4" class="pa-0 pr-2">
                        <v-text-field
                          model-value="+94"
                          label="Code"
                          variant="outlined"
                          density="comfortable"
                          readonly
                          hide-details
                        />
                      </v-col>

                      <v-col cols="10" sm="6" md="6" lg="6" class="pa-0">
                        <v-text-field 
                          v-model="dealer.primary_mobile_number" 
                          :rules="primaryPhoneRules" 
                          label="Primary Phone Number *"
                          placeholder="Type Primary Phone Number"
                          density="comfortable" 
                          variant="outlined"
                          maxlength="10"
                          @input="dealer.primary_mobile_number = dealer.primary_mobile_number.replace(/[^0-9]/g, ''); validatePhoneNumbers()"
                        />
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Secondary Phone Number (Conditional) -->
                  <div v-if="showSecondaryPhone" class="mb-3">
                    <v-row align="start">
                      <v-col cols="12" sm="4" md="4" lg="4" class="pa-0 pr-2">
                        <v-text-field
                          model-value="+94"
                          label="Code"
                          variant="outlined"
                          density="comfortable"
                          readonly
                          hide-details
                        />
                      </v-col>

                      <v-col cols="10" sm="6" md="6" lg="6" class="pa-0">
                        <v-text-field 
                          v-model="dealer.secondary_mobile_number" 
                          :rules="secondaryPhoneRules" 
                          label="Secondary Phone Number"
                          placeholder="Type Secondary Phone Number"
                          density="comfortable" 
                          variant="outlined"
                          maxlength="10"
                          @input="dealer.secondary_mobile_number = dealer.secondary_mobile_number.replace(/[^0-9]/g, ''); validatePhoneNumbers()"
                        />
                      </v-col>

                      <v-col cols="2" sm="2" md="2" lg="2" class="d-flex justify-end pa-0 pl-2">
                        <v-btn 
                          icon size="small"
                          style="background-color: #FCE8E6 !important; border: 1px solid #E57373 !important; color: #D32F2F !important;"
                          variant="outlined" 
                          @click="removeSecondaryPhone"
                        >
                          <v-icon style="color: #D32F2F !important;">mdi-minus-thick</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Add Phone Number Button -->
                  <v-row class="mb-2">
                    <v-col cols="12" class="d-flex">
                      <div style="width: 250px;">
                        <v-btn
                          v-if="!showSecondaryPhone"
                          style="width: 100% !important; background-color: #e6effc !important; border: 1px solid #0000FF !important; color: #0000FF !important;"
                          variant="outlined" 
                          @click="addSecondaryPhone"
                        >
                          <v-icon>mdi-plus-thick</v-icon> Add Phone Number
                        </v-btn>
                        <v-divider class="mt-3"></v-divider>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>

            <!-- Divider -->
            <v-row class="mt-2">
              <v-col cols="12">
                <v-divider></v-divider>
              </v-col>
            </v-row>

            <!-- Map and Location Section -->
            <v-row class="mt-4">
              <!-- Left Column - Map -->
              <v-col cols="12" md="6">
                <p class="custom_page_main_sub_title">
                  <strong>USE MAPS TO SELECT A DEALER LOCATION *</strong>
                </p>
                <p class="custom_page_main_sub_title mt-2" style="opacity: 0.6; line-height: 26px">
                  In here, you must set the dealer location by searching the location or dragging & placing it.
                  This field is required.
                </p>

                <!-- Location validation error message -->
                <v-alert v-if="locationError" type="error" variant="tonal" class="mb-3" dense>
                  {{ locationError }}
                </v-alert>

                <Map ref="map" :center="mapCenter" :zoom="7" style="height: 600px;"
                  class="d-flex custom_outlet_selection_map mt-2 custom_card_box"
                  :class="{ 'map-error': locationError }">
                  <Marker v-if="markerPosition" :icon="customIcon" :position="markerPosition" />
                </Map>
                <v-btn v-if="dealer.lat && dealer.lng" class="position-absolute ml-2 mb-15 custom_lat_lon_label">
                  lat: {{ dealer.lat }} | lng: {{ dealer.lng }}
                </v-btn>
                <v-btn v-else class="position-absolute ml-2 mb-15 custom_lat_lon_label" color="grey">
                  No location selected - Required
                </v-btn>
              </v-col>

              <!-- Right Column - Location Details -->
              <v-col cols="12" md="6">
                <v-card border="md opacity-1" class="mb-4">
                  <v-card-text class="ma-4 mb-0">
                    <v-text-field 
                      append-inner-icon="mdi-magnify" 
                      label="Search Address (Optional)"
                      placeholder="Type Search address" 
                      variant="outlined" 
                      id="search_address"
                      density="comfortable" 
                      class="custom_input_field_color" 
                    />
                  </v-card-text>
                </v-card>

                <!-- LAT and LNG Input Fields -->
                <v-row>
                  <v-col cols="6">
                    <v-text-field 
                      v-model="dealer.lat" 
                      label="Latitude *"
                      placeholder="e.g., 7.863062567888"
                      variant="outlined" 
                      density="comfortable"
                      :rules="[v => !!v || 'Latitude is required', v => !isNaN(parseFloat(v)) || 'Latitude must be a number']"
                      @input="updateMarkerFromLatLng"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field 
                      v-model="dealer.lng" 
                      label="Longitude *"
                      placeholder="e.g., 80.98859753968648"
                      variant="outlined" 
                      density="comfortable"
                      :rules="[v => !!v || 'Longitude is required', v => !isNaN(parseFloat(v)) || 'Longitude must be a number']"
                      @input="updateMarkerFromLatLng"
                    />
                  </v-col>
                </v-row>

                <v-text-field 
                  v-model="dealer.address" 
                  ref="dealerAddressField" 
                  :rules="dealerAddressRules"
                  label="Dealer Address *" 
                  placeholder="Type dealer address" 
                  variant="outlined"
                  density="comfortable" 
                  class="custom_input_field_color mt-4" 
                />

                <v-select 
                  v-model="dealer.region_code" 
                  :items="uniqueRegionCodes"
                  :rules="[v => !!v || 'Region Code is required']" 
                  label="Region Code *"
                  placeholder="Select Region Code" 
                  variant="outlined" 
                  density="comfortable"
                  class="custom_input_field_color mt-4" 
                  @update:modelValue="onRegionChange" 
                />

                <v-select 
                  v-model="dealer.area_code" 
                  :items="filteredAreaCodes" 
                  :loading="areaCodeLoading"
                  :rules="[v => !!v || 'Area Code is required']" 
                  label="Area Code *"
                  placeholder="Select Area Code" 
                  variant="outlined" 
                  density="comfortable"
                  class="custom_input_field_color mt-4" 
                />

                <!-- Updated Dealer Type Dropdown with Image Data -->
                <v-select 
                  v-model="dealer.dealer_type" 
                  :items="dealerTypeOptions"
                  :rules="[v => !!v || 'Dealer Type is required']" 
                  label="Dealer Type *"
                  placeholder="Select Dealer Type" 
                  variant="outlined" 
                  density="comfortable"
                  class="custom_input_field_color mt-4" 
                />

                <v-select 
                  v-model="dealer.piyestra_company" 
                  :items="piyestraCompanies" 
                  label="Piyestra Company *"
                  :rules="[v => !!v && v.length > 0 || 'At least one Piyestra Company is required']"
                  multiple 
                  chips 
                  closable-chips 
                  variant="outlined" 
                  density="comfortable"
                  class="custom_input_field_color mt-4" 
                  @update:modelValue="handlePiyestraCompaniesChange" 
                />

                <div class="mt-3">
                  <div v-for="company in dealer.piyestra_company" :key="company"
                    class="d-flex align-center mb-2">
                    <v-chip color="primary" outlined class="mr-2">
                      {{ company }}
                    </v-chip>
                    <v-icon class="mr-3 cursor-pointer" @click="editPiyestraCompany(company)">mdi-pencil</v-icon>

                    <span class="caption grey--text text--darken-2 ml-2">
                      <template v-if="piyestraSettings[company]">
                        Credit Limit: {{ piyestraSettings[company].credit_limit_type || '-' }} |
                        Credit Period: {{ piyestraSettings[company].credit_period_type || '-' }} |
                        Cash Limit: {{ piyestraSettings[company].cash_limit_type || '-' }} |
                        Cash Period: {{ piyestraSettings[company].cash_period_type || '-' }}
                      </template>
                      <template v-else>
                        (not configured)
                      </template>
                    </span>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>
        
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            v-if="permissions.write"
            style="background-color: #800080; color: white;"
            class="px-8"
            size="large"
            rounded="sm"
            @click="createDealer"
            :disabled="loading"
          >
            <v-progress-circular v-if="loading" indeterminate size="20" color="white" class="mr-2"></v-progress-circular>
            CREATE NEW DEALER
          </v-btn>
          <v-btn
            variant="outlined"
            color="grey"
            class="px-8 ml-3"
            size="large"
            rounded="sm"
            @click="cancelPageDealer"
            :disabled="loading"
          >
            CANCEL
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Company Configuration Dialog -->
      <v-dialog v-model="newCompanyDialog" max-width="600px" persistent>
        <v-card>
          <v-card-title class="headline">
            Configure {{ currentNewCompany }}
          </v-card-title>
          <v-card-text>
            <v-text-field 
              v-model="piyestraForm.credit_limit_type" 
              label="Credit Limit Type *"
              :rules="[v => !!v || 'Credit Limit Type is required', v => validateNumericOnly(v, 'Credit Limit')]"
              placeholder="e.g. 500000, 50%, Unlimited" 
              variant="outlined" 
              density="comfortable" 
              required
              @input="piyestraForm.credit_limit_type = allowOnlyNumbers(piyestraForm.credit_limit_type)" />
            <v-text-field 
              v-model="piyestraForm.credit_period_type" 
              label="Credit Period Type *"
              :rules="[v => !!v || 'Credit Period Type is required', v => validateNumericOnly(v, 'Credit Period')]"
              placeholder="e.g. 61 Days" 
              variant="outlined" 
              density="comfortable" 
              required
              @input="piyestraForm.credit_period_type = allowOnlyNumbers(piyestraForm.credit_period_type)" />
            <v-text-field 
              v-model="piyestraForm.cash_limit_type" 
              label="Cash Limit Type *"
              :rules="[v => !!v || 'Cash Limit Type is required', v => validateNumericOnly(v, 'Cash Limit')]"
              placeholder="e.g. 200000, No Limit" 
              variant="outlined" 
              density="comfortable" 
              required
              @input="piyestraForm.cash_limit_type = allowOnlyNumbers(piyestraForm.cash_limit_type)" />
            <v-text-field 
              v-model="piyestraForm.cash_period_type" 
              label="Cash Period Type *"
              :rules="[v => !!v || 'Cash Period Type is required', v => validateNumericOnly(v, 'Cash Period')]"
              placeholder="e.g. 4 Days"
              variant="outlined" 
              density="comfortable" 
              required
              @input="piyestraForm.cash_period_type = allowOnlyNumbers(piyestraForm.cash_period_type)" />
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="cancelNewCompany">Cancel</v-btn>
            <v-btn color="primary" text @click="saveNewCompany">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Edit Company Dialog -->
      <v-dialog v-model="editCompanyDialog" max-width="600px" persistent>
        <v-card>
          <v-card-title class="headline">
            Edit {{ currentEditingCompany }}
          </v-card-title>
          <v-card-text>
            <v-text-field 
              v-model="piyestraForm.credit_limit_type" 
              label="Credit Limit *"
              :rules="[v => !!v || 'Credit Limit is required', v => validateNumericOnly(v, 'Credit Limit')]"
              placeholder="e.g. 500000, 50%, Unlimited" 
              variant="outlined" 
              density="comfortable" 
              required
              @input="piyestraForm.credit_limit_type = allowOnlyNumbers(piyestraForm.credit_limit_type)" />
            <v-text-field 
              v-model="piyestraForm.credit_period_type" 
              label="Credit Period *"
              :rules="[v => !!v || 'Credit Period is required', v => validateNumericOnly(v, 'Credit Period')]"
              placeholder="e.g. 61 Days" 
              variant="outlined" 
              density="comfortable" 
              required
              @input="piyestraForm.credit_period_type = allowOnlyNumbers(piyestraForm.credit_period_type)" />
            <v-text-field 
              v-model="piyestraForm.cash_limit_type" 
              label="Cash Limit *"
              :rules="[v => !!v || 'Cash Limit is required', v => validateNumericOnly(v, 'Cash Limit')]"
              placeholder="e.g. 200000, No Limit" 
              variant="outlined" 
              density="comfortable" 
              required
              @input="piyestraForm.cash_limit_type = allowOnlyNumbers(piyestraForm.cash_limit_type)" />
            <v-text-field 
              v-model="piyestraForm.cash_period_type" 
              label="Cash Period *"
              :rules="[v => !!v || 'Cash Period is required', v => validateNumericOnly(v, 'Cash Period')]"
              placeholder="e.g. 4 Days"
              variant="outlined" 
              density="comfortable" 
              required
              @input="piyestraForm.cash_period_type = allowOnlyNumbers(piyestraForm.cash_period_type)" />
          </v-card-text>
          
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="cancelEditCompany">Cancel</v-btn>
            <v-btn color="primary" text @click="updateCompany">Update</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- DEALER Bulk Upload Dialog -->
      <v-dialog v-model="dealerBulkUploadDialog" fullscreen id="app_user_list" content-class="d-flex flex-column">
        <v-card flat class="d-flex flex-column" style="height: 100vh;">
          <v-toolbar style="border-radius: 0px !important;" class="custom_fullscreen_dialog_toolbar" color="white" dense flat>
            <v-toolbar-title>
              <v-img class="custom_company_logo_nav" :src="require('@/img/company_logo/logo-dark 2.png')"
                @click="homeReload()"></v-img>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn style="background-color: #800080; color: white;" variant="flat" @click="closeBulkUploadDialog">
              CLOSE
            </v-btn>
          </v-toolbar>

          <v-container fluid class="pa-0 flex-grow-1" style="overflow-y: auto;">
            <v-row class="ma-0">
              <v-col cols="12" md="12" class="pa-6">
                <v-card class="pa-0 mx-6 mt-2 custom_card_box" border="sm opacity-1">
                  <template v-slot:prepend>
                    <v-icon>mdi-view-dashboard</v-icon>
                    <v-card-title class="font-weight-bold custom_page_main_sub_title ml-3">UPLOAD
                      SPREADSHEET</v-card-title>
                  </template>
                </v-card>

                <v-card class="pa-3 mx-6 custom_card_box mt-4" border="sm opacity-1">
                  <v-row class="ma-0">
                    <!-- Left Column - Upload Section -->
                    <v-col cols="12" xs="12" sm="12" md="5" lg="5">
                      <v-row>
                        <v-col cols="12">
                          <p class="text-body-1 font-weight-medium mb-4 mt-2 custom_page_component_main_title">
                            Upload your dealer bulk data set using excel or a csv file.
                          </p>
                        </v-col>
                      </v-row>

                      <v-divider></v-divider>

                      <v-row class="mt-4">
                        <v-col cols="12">
                          <v-file-input ref="fileInput" accept=".csv, .xls, .xlsx" clearable density="comfortable"
                            class="custom_input_field_color" label="Upload your Excel or CSV file *" counter show-size
                            variant="outlined" v-model="selectedFile" @update:modelValue="handleFileUpload"
                            :loading="uploading"></v-file-input>
                        </v-col>
                      </v-row>

                      <v-row class="mt-2">
                        <v-col cols="12">
                          <v-btn :disabled="!selectedFile || uploading || bulkItems.length === 0" :loading="uploading"
                            style="background-color: #800080; color: white;" size="large" block
                            @click="uploadBulkDealers">
                            <v-icon left>mdi-cloud-upload</v-icon>
                            UPLOAD DEALERS
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-col>

                    <!-- Divider Column -->
                    <v-col class="d-flex align-center justify-center" cols="12" sm="12" md="1" lg="1">
                      <v-divider :thickness="2" vertical class="d-none d-md-flex"></v-divider>
                      <v-divider :thickness="2" class="d-flex d-md-none my-4"></v-divider>
                    </v-col>

                    <!-- Right Column - Template Download Section -->
                    <v-col cols="12" xs="12" sm="12" md="6" lg="6">
                      <v-card class="d-flex align-center justify-space-between pa-4 mt-2" color="#F0F0F0" flat
                        width="100%">
                        <div class="mr-4">
                          <p class="mb-0 text-body-2 font-weight-bold">DOWNLOAD SAMPLE TEMPLATE</p>
                          <p class="text-caption mb-0 mt-1" style="opacity: 0.7">Get started with our pre-formatted
                            template</p>
                        </div>
                        <v-divider vertical class="mx-4"></v-divider>
                        <v-btn style="background-color: #800080; color: white;" size="large" variant="flat"
                          @click="downloadTemplate">
                          <v-icon left>mdi-download</v-icon>
                          DOWNLOAD
                        </v-btn>
                      </v-card>

                      <v-card class="mt-4" color="#F5F5F5" flat>
                        <v-card-text>
                          <p class="font-weight-bold custom_page_component_main_title mb-3">File Format Requirements:
                          </p>
                          <v-list density="compact" bg-color="transparent">
                            <v-list-item>
                              <template v-slot:prepend>
                                <v-icon color="#800080" size="small">mdi-check-circle</v-icon>
                              </template>
                              <v-list-item-title class="text-body-2">Accepted formats: .csv, .xls,
                                .xlsx</v-list-item-title>
                            </v-list-item>
                            <v-list-item>
                              <template v-slot:prepend>
                                <v-icon color="#800080" size="small">mdi-check-circle</v-icon>
                              </template>
                              <v-list-item-title class="text-body-2">Maximum file size: 12MB</v-list-item-title>
                            </v-list-item>
                            <v-list-item>
                              <template v-slot:prepend>
                                <v-icon color="#800080" size="small">mdi-check-circle</v-icon>
                              </template>
                              <v-list-item-title class="text-body-2">Required fields: dealer_name, dealer_code,
                                company_name,
                                region_code, area_code, address, dealer_type</v-list-item-title>
                            </v-list-item>
                            <v-list-item>
                              <template v-slot:prepend>
                                <v-icon color="#800080" size="small">mdi-check-circle</v-icon>
                              </template>
                              <v-list-item-title class="text-body-2">Optional fields: mobile_number, mobile_number2,
                                company, credit_limit_type, credit_period_type, cash_limit_type, cash_period_type, lat,
                                lng</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>

                  <!-- Preview Section -->
                  <v-card v-if="bulkItems.length > 0" flat class="mt-6">
                    <v-card-text>
                      <v-divider class="mb-4"></v-divider>
                      <div class="d-flex justify-space-between align-center mb-4">
                        <div>
                          <h3 class="custom_page_component_main_title">Preview Uploaded Data</h3>
                          <p class="text-caption mt-1" style="opacity: 0.7">Total items: {{ bulkItems.length }}</p>
                        </div>
                        <v-btn size="small" color="error" variant="text" @click="clearPreview">
                          <v-icon left size="small">mdi-close</v-icon> Clear Preview
                        </v-btn>
                      </div>
                      <v-data-table :headers="bulkHeaders" :items="bulkItems" :items-per-page="5"
                        class="custom_table_normal">
                        <template v-slot:[`item.status`]="{ item }">
                          <v-chip :color="item.status === 'Valid' ? 'green' : 'warning'" size="small" variant="tonal"
                            class="font-weight-medium">
                            {{ item.status }}
                          </v-chip>
                        </template>
                      </v-data-table>
                    </v-card-text>
                  </v-card>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbar" :color="snackbarColor" location="top right" outlined timeout="3000">
        {{ snackbarMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn icon v-bind="attrs" @click="snackbar = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>

      <v-overlay v-model="loading" :scrim="false" class="custom_centered_overlay">
        <v-progress-circular color="#800080" indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </v-container>
  </v-app>
</template>

<script>
import axios from 'axios';
import { Map, Marker } from '@fawmi/vue-google-maps';
import * as XLSX from 'xlsx';
import { getPermissions } from "@/plugins/permissions";

export default {
  components: { Map, Marker },

  data: () => ({
    permissions: { read: false, write: false, delete: false },
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: '',
    loading: false,
    uploading: false,
    locationError: '',
    phoneNumberError: '',
    showSecondaryPhone: false,

    focused: {
      dealer_name: false,
      company_name: false,
      dealer_code: false,
      address: false,
      region_code: false,
      area_code: false
    },

    dealer: {
      dealer_name: '',
      dealer_code: '',
      company_name: '',
      address: '',
      lat: null,
      lng: null,
      region_code: null,
      area_code: null,
      dealer_type: null,
      primary_mobile_number: '',
      secondary_mobile_number: '',
      piyestra_company: []
    },

    // Updated Dealer Type Options based on the provided image
    dealerTypeOptions: [
      ' Furniture & Electronic',
      'Furniture',
      'Electronic',
      'Hardware',
      'Super Center-Daxer',
      'Book Shop',
      'Delivery & Government Suppliers',
      'Non Transaction'
    ],

    mapCenter: { lat: 7.863062567888, lng: 80.98859753968648 },
    markerPosition: null,
    customIcon: {
      url: require('@/assets/ICONS/maker.png'),
      scaledSize: { width: 35, height: 40 }
    },

    allAreasData: [],
    uniqueRegionCodes: [],
    filteredAreaCodes: [],
    areaCodeLoading: false,

    dealerBulkUploadDialog: false,
    selectedFile: null,
    bulkFile: null,
    bulkItems: [],
    itemsPerPage: 10,
    bulkHeaders: [
      { title: 'Dealer Name', key: 'Dealer Name', sortable: true },
      { title: 'Dealer Code', key: 'Dealer Code', sortable: true },
      { title: 'Company Name', key: 'Company Name', sortable: true },
      { title: 'Dealer Type', key: 'Dealer Type', sortable: true },
      { title: 'Address', key: 'Dealer Address', sortable: false },
      { title: 'Region Code', key: 'Region Code', sortable: true },
      { title: 'Area Code', key: 'Area Code', sortable: true },
      { title: 'Primary Mobile', key: 'Primary Mobile', sortable: false },
      { title: 'Secondary Mobile', key: 'Secondary Mobile', sortable: false },
      { title: 'Status', key: 'status', sortable: false }
    ],

    phoneRules: [
      v => !v || /^\d+$/.test(v) || "Only numbers are allowed",
      v => !v || v.length <= 10 || "Maximum 10 digits allowed",
      v => !v || !/^0+$/.test(v) || "Phone number cannot be all zeros"
    ],
    primaryPhoneRules: [
      v => !!v || "Primary phone number is required",
      v => /^\d+$/.test(v) || "Only numbers are allowed",
      v => v.length <= 10 || "Maximum 10 digits allowed",
      v => !/^0+$/.test(v) || "Phone number cannot be all zeros"
    ],
    secondaryPhoneRules: [
      v => !v || /^\d+$/.test(v) || "Only numbers are allowed",
      v => !v || v.length <= 10 || "Maximum 10 digits allowed",
      v => !v || !/^0+$/.test(v) || "Phone number cannot be all zeros",
      v => {
        if (!v) return true;
        const primary = this.dealer.primary_mobile_number || '';
        if (v === primary) {
          return 'Secondary phone number cannot be the same as primary phone number';
        }
        return true;
      }
    ],
    countryCodeRules: [v => !!v || "Code is required"],

    piyestraCompanies: [
      'D.R.Home Appliances (Pvt) Ltd',
      'Piyestra Furniture (Pvt) Ltd',
      'D.R.Industries Electronics',
    ],

    newCompanyDialog: false,
    editCompanyDialog: false,
    currentNewCompany: '',
    currentEditingCompany: '',
    piyestraSettings: {},
    piyestraForm: {
      credit_limit_type: '',
      credit_period_type: '',
      cash_limit_type: '',
      cash_period_type: ''
    }
  }),

  computed: {
    dealerNameRules() {
      return [
        v => !!(v && v.trim()) || 'Dealer Name is required',
        v => /^[A-Za-z\s]+$/.test(v || '') || 'Dealer Name can only contain letters and spaces (no numbers or special characters)',
        v => ((v || '').trim().length >= 2) || 'Dealer Name must be at least 2 characters long',
        v => ((v || '').trim().length <= 100) || 'Dealer Name cannot exceed 100 characters',
        v => !/\s{2,}/.test((v || '').trim()) || 'Dealer Name cannot contain multiple consecutive spaces'
      ];
    },
    dealerCodeRules() {
      return [
        v => !!v || 'Dealer Code is required',
        v => (v && v.length >= 3) || 'Dealer Code must be at least 3 characters',
        v => /^[A-Za-z0-9_-]+$/.test(v) || 'Dealer Code can only contain letters, numbers, underscores and hyphens'
      ];
    },
    dealerAddressRules() {
      return [v => !!v || 'Dealer Address is required'];
    },
    companyNameRules() {
      return [
        v => !!v || 'Company / Institute Name is required',
        v => (v && v.length >= 2) || 'Company name must be at least 2 characters long',
        v => (v && v.length <= 200) || 'Company name cannot exceed 200 characters',
        v => !/^\s/.test(v) || 'Company name cannot start with a space',
        v => !/\s$/.test(v) || 'Company name cannot end with a space'
      ];
    }
  },

  mounted() {
    this.permissions = getPermissions(0, "Module Name");

    if (!this.permissions.read) {
      this.showSnackbar ? this.showSnackbar("Access Denied", "error") : alert("Access Denied");
      this.$router.push('/dashboard');
    }
    this.fetchAreasData();
    if (this.$refs.map) {
      this.$refs.map.$mapPromise.then(map => this.mapInit(map)).catch(err => {
        console.log('Map not available or not required', err);
      });
    }

    if (this.$route.query.openBulkUpload === 'true') {
      this.dealerBulkUploadDialog = true;
    }
  },

  methods: {
    // New method to update marker from LAT/LNG inputs
    updateMarkerFromLatLng() {
      const lat = parseFloat(this.dealer.lat);
      const lng = parseFloat(this.dealer.lng);
      
      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        this.markerPosition = { lat, lng };
        this.mapCenter = { lat, lng };
        this.locationError = '';
        
        // Update map center if map is available
        if (this.$refs.map) {
          this.$refs.map.$mapPromise.then(map => {
            map.setCenter({ lat, lng });
            map.setZoom(15);
          }).catch(err => {
            console.log('Error updating map:', err);
          });
        }
      } else if (this.dealer.lat || this.dealer.lng) {
        // Clear marker if coordinates are invalid
        if (isNaN(lat) || isNaN(lng)) {
          this.markerPosition = null;
        }
      }
    },

    onRegionChange(selectedRegion) {
      this.dealer.area_code = null;

      if (!selectedRegion) {
        this.filteredAreaCodes = [];
        return;
      }

      this.areaCodeLoading = true;

      const areasForRegion = this.allAreasData.filter(area =>
        area.region_code === selectedRegion
      );

      this.filteredAreaCodes = areasForRegion.map(area => ({
        title: area.area_code,
        value: area.area_code,
        description: area.description
      })).sort((a, b) => a.title.localeCompare(b.title));

      this.areaCodeLoading = false;

      if (this.filteredAreaCodes.length === 1) {
        this.dealer.area_code = this.filteredAreaCodes[0].value;
        this.snackbarMessage = `Auto-selected area code: ${this.dealer.area_code}`;
        this.snackbarColor = 'info';
        this.snackbar = true;
      }
    },

    allowOnlyNumbers(value) {
      if (!value) return value;
      return value.replace(/[^0-9]/g, '');
    },

    validateNumericOnly(value, fieldName) {
      if (!value || value.trim() === '') return true;
      if (/^\d+$/.test(value.trim())) {
        return true;
      }
      return `${fieldName} must contain only numbers`;
    },

    showSnackbar(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    validateDealerName() {
      let cleaned = this.dealer.dealer_name.replace(/[^A-Za-z\s]/g, '');
      if (this.dealer.dealer_name !== cleaned) {
        this.dealer.dealer_name = cleaned;
      }
    },

    getUserIdFromStorage() {
      const userId = localStorage.getItem('userId');
      if (userId) {
        console.log('✅ Found userId in localStorage:', userId);
        return parseInt(userId);
      }
      return null;
    },

    addSecondaryPhone() {
      this.showSecondaryPhone = true;
    },

    removeSecondaryPhone() {
      this.showSecondaryPhone = false;
      this.dealer.secondary_mobile_number = '';
      this.phoneNumberError = '';
    },

    validatePhoneNumbers() {
      this.phoneNumberError = '';
      
      const primaryPhone = this.dealer.primary_mobile_number?.trim() || '';
      const secondaryPhone = this.dealer.secondary_mobile_number?.trim() || '';
      
      if (primaryPhone && secondaryPhone && primaryPhone === secondaryPhone) {
        this.phoneNumberError = 'Primary and secondary phone numbers cannot be the same';
        return false;
      }
      
      return true;
    },

    async fetchAreasData() {
      this.areaCodeLoading = true;
      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.VUE_APP_API_BASE_URL || process.env.VUE_APP_API_BASE_URL;
        const response = await axios.get(`${baseUrl}/api/area-allList`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success && response.data.data) {
          this.allAreasData = response.data.data;

          const regionSet = new Set();
          this.allAreasData.forEach(area => {
            if (area.region_code) {
              regionSet.add(area.region_code);
            }
          });
          this.uniqueRegionCodes = Array.from(regionSet).sort();
        } else {
          console.error('Failed to load areas data:', response.data.message);
          this.uniqueRegionCodes = [];
        }
      } catch (err) {
        console.error('Error fetching areas data:', err);
        this.snackbarMessage = 'Failed to load region and area data';
        this.snackbarColor = 'error';
        this.snackbar = true;
        this.uniqueRegionCodes = [];
      } finally {
        this.areaCodeLoading = false;
      }
    },

    mapInit(map) {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.log('Google Maps not fully loaded');
        return;
      }

      const input = document.getElementById("search_address");
      if (!input) return;

      const autocomplete = new window.google.maps.places.SearchBox(input);

      map.addListener("bounds_changed", () => autocomplete.setBounds(map.getBounds()));

      autocomplete.addListener("places_changed", () => {
        const places = autocomplete.getPlaces();
        if (!places.length) return;

        const place = places[0];
        if (!place.geometry?.location) return;

        this.dealer.lat = place.geometry.location.lat();
        this.dealer.lng = place.geometry.location.lng();
        this.markerPosition = { lat: this.dealer.lat, lng: this.dealer.lng };
        this.locationError = '';

        if (place.geometry.viewport) {
          map.fitBounds(place.geometry.viewport);
        } else {
          map.setCenter(place.geometry.location);
          map.setZoom(17);
        }
      });

      window.google.maps.event.addListener(map, 'click', e => {
        this.dealer.lat = e.latLng.lat();
        this.dealer.lng = e.latLng.lng();
        this.markerPosition = { lat: this.dealer.lat, lng: this.dealer.lng };
        this.locationError = '';
      });
    },

    handlePiyestraCompaniesChange(newValue) {
      const oldValue = this.dealer.piyestra_company || [];
      const added = newValue.filter(c => !oldValue.includes(c));

      if (added.length > 0) {
        this.currentNewCompany = added[0];
        this.piyestraForm = {
          credit_limit_type: '',
          credit_period_type: '',
          cash_limit_type: '',
          cash_period_type: ''
        };
        this.newCompanyDialog = true;
      }

      this.dealer.piyestra_company = [...newValue];
    },

    saveNewCompany() {
      if (!this.piyestraForm.credit_limit_type || !this.piyestraForm.credit_limit_type.trim()) {
        this.showSnackbar('Credit Limit Type is required', 'error');
        return;
      }
      if (!this.piyestraForm.credit_period_type || !this.piyestraForm.credit_period_type.trim()) {
        this.showSnackbar('Credit Period Type is required', 'error');
        return;
      }
      if (!this.piyestraForm.cash_limit_type || !this.piyestraForm.cash_limit_type.trim()) {
        this.showSnackbar('Cash Limit Type is required', 'error');
        return;
      }
      if (!this.piyestraForm.cash_period_type || !this.piyestraForm.cash_period_type.trim()) {
        this.showSnackbar('Cash Period Type is required', 'error');
        return;
      }
      
      this.piyestraSettings = {
        ...this.piyestraSettings,
        [this.currentNewCompany]: { ...this.piyestraForm }
      };
      this.newCompanyDialog = false;
      this.showSnackbar('Company configuration saved successfully!', 'success');
    },

    cancelNewCompany() {
      const index = this.dealer.piyestra_company.indexOf(this.currentNewCompany);
      if (index > -1) {
        this.dealer.piyestra_company.splice(index, 1);
      }
      this.newCompanyDialog = false;
    },

    editPiyestraCompany(company) {
      this.currentEditingCompany = company;
      this.piyestraForm = {
        credit_limit_type: this.piyestraSettings[company]?.credit_limit_type || '',
        credit_period_type: this.piyestraSettings[company]?.credit_period_type || '',
        cash_limit_type: this.piyestraSettings[company]?.cash_limit_type || '',
        cash_period_type: this.piyestraSettings[company]?.cash_period_type || ''
      };
      this.editCompanyDialog = true;
    },

    updateCompany() {
      if (!this.piyestraForm.credit_limit_type || !this.piyestraForm.credit_limit_type.trim()) {
        this.showSnackbar('Credit Limit is required', 'error');
        return;
      }
      if (!this.piyestraForm.credit_period_type || !this.piyestraForm.credit_period_type.trim()) {
        this.showSnackbar('Credit Period is required', 'error');
        return;
      }
      if (!this.piyestraForm.cash_limit_type || !this.piyestraForm.cash_limit_type.trim()) {
        this.showSnackbar('Cash Limit is required', 'error');
        return;
      }
      if (!this.piyestraForm.cash_period_type || !this.piyestraForm.cash_period_type.trim()) {
        this.showSnackbar('Cash Period is required', 'error');
        return;
      }

      this.piyestraSettings = {
        ...this.piyestraSettings,
        [this.currentEditingCompany]: { ...this.piyestraForm }
      };
      this.editCompanyDialog = false;
      this.showSnackbar('Company settings updated successfully!', 'success');
    },

    cancelEditCompany() {
      this.editCompanyDialog = false;
    },

    validateLocation() {
      if (!this.dealer.lat || !this.dealer.lng) {
        this.locationError = 'Please select a location on the map or enter coordinates. This is required.';
        return false;
      }
      
      const lat = parseFloat(this.dealer.lat);
      const lng = parseFloat(this.dealer.lng);
      
      if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        this.locationError = 'Invalid coordinates. Latitude must be between -90 and 90, Longitude between -180 and 180.';
        return false;
      }
      
      this.locationError = '';
      return true;
    },

    async createDealer() {
      this.loading = true;

      if (!this.validateLocation()) {
        this.snackbarMessage = this.locationError;
        this.snackbarColor = 'error';
        this.snackbar = true;
        this.loading = false;
        return;
      }

      if (!this.dealer.company_name || this.dealer.company_name.trim() === '') {
        this.snackbarMessage = 'Company / Institute Name is required';
        this.snackbarColor = 'error';
        this.snackbar = true;
        this.loading = false;
        return;
      }

      if (!this.dealer.dealer_type) {
        this.snackbarMessage = 'Dealer Type is required';
        this.snackbarColor = 'error';
        this.snackbar = true;
        this.loading = false;
        return;
      }

      if (!this.dealer.piyestra_company || this.dealer.piyestra_company.length === 0) {
        this.snackbarMessage = 'At least one Piyestra Company is required';
        this.snackbarColor = 'error';
        this.snackbar = true;
        this.loading = false;
        return;
      }

      const primaryPhone = this.dealer.primary_mobile_number?.trim() || '';
      if (!primaryPhone) {
        this.snackbarMessage = 'Primary phone number is required';
        this.snackbarColor = 'error';
        this.snackbar = true;
        this.loading = false;
        return;
      }

      const secondaryPhone = this.dealer.secondary_mobile_number?.trim() || '';
      if (primaryPhone && secondaryPhone && primaryPhone === secondaryPhone) {
        this.snackbarMessage = 'Primary and secondary phone numbers cannot be the same';
        this.snackbarColor = 'error';
        this.snackbar = true;
        this.loading = false;
        return;
      }

      if (this.dealer.piyestra_company.length > 0) {
        const unconfigured = this.dealer.piyestra_company.filter(c => !this.piyestraSettings[c]);
        if (unconfigured.length > 0) {
          this.snackbarMessage = `Please configure settings for: ${unconfigured.join(', ')}`;
          this.snackbarColor = 'error';
          this.snackbar = true;
          this.loading = false;
          return;
        }
      }

      const userId = this.getUserIdFromStorage();
      if (!userId) {
        this.snackbarMessage = 'User ID not found. Please log in again.';
        this.snackbarColor = 'error';
        this.snackbar = true;
        this.loading = false;
        return;
      }

      const payload = {
        userId: userId,
        dealer_name: this.dealer.dealer_name.trim().replace(/\s{2,}/g, ' '),
        dealer_code: this.dealer.dealer_code,
        company_name: this.dealer.company_name || '',
        address: this.dealer.address,
        region_code: this.dealer.region_code,
        area_code: this.dealer.area_code,
        dealer_type: this.dealer.dealer_type,
        mobile_number: primaryPhone,
        mobile_number2: secondaryPhone,
        lat: this.dealer.lat.toString(),
        lng: this.dealer.lng.toString(),
        piyestra_company: this.dealer.piyestra_company.map(company => ({
          company: company,
          credit_limit_type: this.piyestraSettings[company]?.credit_limit_type || '',
          credit_period_type: this.piyestraSettings[company]?.credit_period_type || '',
          cash_limit_type: this.piyestraSettings[company]?.cash_limit_type || '',
          cash_period_type: this.piyestraSettings[company]?.cash_period_type || ''
        }))
      };

      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.VUE_APP_API_BASE_URL || process.env.VUE_APP_API_BASE_URL;
        const response = await axios.post(
          `${baseUrl}/api/createDealer`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          this.snackbarMessage = response.data.message || 'Dealer created successfully!';
          this.snackbarColor = 'success';
          this.snackbar = true;

          setTimeout(() => {
            this.$router.push('/dealer-list');
          }, 1500);
        } else {
          throw new Error(response.data.message || 'Failed to create dealer');
        }
      } catch (err) {
        console.error('Error creating dealer:', err);
        this.snackbarMessage = err.response?.data?.message || err.message || 'Error creating dealer. Please try again.';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.loading = false;
      }
    },

    handleFileUpload(file) {
      if (!file) {
        this.selectedFile = null;
        this.bulkFile = null;
        return;
      }

      const uploadedFile = file instanceof File ? file : file.target?.files[0];
      if (!uploadedFile) return;

      this.bulkFile = uploadedFile;
      this.selectedFile = uploadedFile;

      if (uploadedFile.size > 12 * 1024 * 1024) {
        this.snackbarMessage = 'File size exceeds 12MB limit';
        this.snackbarColor = 'error';
        this.snackbar = true;
        this.clearPreview();
        return;
      }

      const reader = new FileReader();
      reader.onload = e => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });

          const headers = json[0].map(h => h?.toString().trim() || '');

          const requiredFields = ['dealer_name', 'dealer_code', 'company_name', 'region_code', 'area_code', 'address', 'dealer_type'];

          const missingFields = requiredFields.filter(field =>
            !headers.some(h => h.toLowerCase().replace(/\s+/g, '_') === field.toLowerCase())
          );

          if (missingFields.length > 0) {
            this.snackbarMessage = `Invalid file format. Missing required fields: ${missingFields.join(', ')}`;
            this.snackbarColor = 'error';
            this.snackbar = true;
            this.clearPreview();
            return;
          }

          const headerMap = {};
          headers.forEach(header => {
            const normalized = header.toLowerCase().replace(/\s+/g, '_');
            if (normalized === 'dealer_name') headerMap['Dealer Name'] = header;
            else if (normalized === 'dealer_code') headerMap['Dealer Code'] = header;
            else if (normalized === 'company_name') headerMap['Company Name'] = header;
            else if (normalized === 'dealer_type') headerMap['Dealer Type'] = header;
            else if (normalized === 'address') headerMap['Dealer Address'] = header;
            else if (normalized === 'region_code') headerMap['Region Code'] = header;
            else if (normalized === 'area_code') headerMap['Area Code'] = header;
            else if (normalized === 'mobile_number') headerMap['Primary Mobile'] = header;
            else if (normalized === 'mobile_number2') headerMap['Secondary Mobile'] = header;
            else if (normalized === 'company') headerMap['Company'] = header;
            else if (normalized === 'credit_limit_type') headerMap['Credit Limit Type'] = header;
            else if (normalized === 'credit_period_type') headerMap['Credit Period Type'] = header;
            else if (normalized === 'cash_limit_type') headerMap['Cash Limit Type'] = header;
            else if (normalized === 'cash_period_type') headerMap['Cash Period Type'] = header;
            else if (normalized === 'lat') headerMap['Latitude'] = header;
            else if (normalized === 'lng') headerMap['Longitude'] = header;
          });

          this.bulkItems = json.slice(1).map((row, index) => {
            const item = {};

            Object.keys(headerMap).forEach(displayName => {
              const originalHeader = headerMap[displayName];
              const headerIndex = headers.indexOf(originalHeader);
              if (headerIndex !== -1 && row[headerIndex] != null) {
                item[displayName] = String(row[headerIndex]).trim();
              } else {
                item[displayName] = '';
              }
            });

            item.status = this.validateBulkItem(item) ? 'Valid' : 'Invalid';
            item._index = index + 1;

            return item;
          });

          const invalidCount = this.bulkItems.filter(item => item.status === 'Invalid').length;
          if (invalidCount > 0) {
            this.snackbarMessage = `${invalidCount} row(s) have invalid data. Please check and correct.`;
            this.snackbarColor = 'warning';
            this.snackbar = true;
          }
        } catch (error) {
          console.error('Error parsing file:', error);
          this.snackbarMessage = 'Error parsing file. Please check the file format.';
          this.snackbarColor = 'error';
          this.snackbar = true;
          this.clearPreview();
        }
      };
      reader.readAsArrayBuffer(uploadedFile);
    },

    validateBulkItem(item) {
      if (!item['Dealer Name'] || item['Dealer Name'].trim() === '') return false;
      if (!item['Dealer Code'] || item['Dealer Code'].trim() === '') return false;
      if (!item['Company Name'] || item['Company Name'].trim() === '') return false;
      if (!item['Dealer Type'] || item['Dealer Type'].trim() === '') return false;
      if (!item['Region Code'] || item['Region Code'].trim() === '') return false;
      if (!item['Area Code'] || item['Area Code'].trim() === '') return false;
      if (!item['Dealer Address'] || item['Dealer Address'].trim() === '') return false;
      return true;
    },

    clearPreview() {
      this.bulkItems = [];
      this.bulkFile = null;
      this.selectedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.reset();
      }
    },

    async uploadBulkDealers() {
      if (!this.bulkFile || this.bulkItems.length === 0) {
        this.snackbarMessage = 'Please select a valid file to upload';
        this.snackbarColor = 'error';
        this.snackbar = true;
        return;
      }

      const invalidItems = this.bulkItems.filter(item => item.status === 'Invalid');
      if (invalidItems.length > 0) {
        this.snackbarMessage = `Cannot upload. Please fix ${invalidItems.length} invalid row(s) first.`;
        this.snackbarColor = 'error';
        this.snackbar = true;
        return;
      }

      this.uploading = true;

      try {
        const token = localStorage.getItem('token');
        const userId = this.getUserIdFromStorage();
        if (!userId) {
          throw new Error('User ID not found. Please log in again.');
        }

        const formData = new FormData();
        formData.append('userId', userId);
        formData.append('file', this.bulkFile);

        const baseUrl = process.env.VUE_APP_API_BASE_URL || process.env.VUE_APP_API_BASE_URL;
        const response = await axios.post(
          `${baseUrl}/api/dealer/bulk-upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (response.data.success) {
          this.snackbarMessage = response.data.message || 'Bulk upload successful!';
          this.snackbarColor = 'success';
          this.snackbar = true;

          setTimeout(() => {
            this.closeBulkUploadDialog();
            this.$router.push('/dealer-list');
          }, 1500);
        } else {
          throw new Error(response.data.message || 'Failed to upload bulk dealers');
        }
      } catch (err) {
        console.error('Bulk upload error:', err);
        this.snackbarMessage = err.response?.data?.message || err.message || 'Bulk upload failed. Please check your file and try again.';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.uploading = false;
      }
    },

    backPageDealer() {
      this.$router.back();
    },

    cancelPageDealer() {
      this.dealer = {
        dealer_name: '',
        dealer_code: '',
        company_name: '',
        address: '',
        lat: null,
        lng: null,
        region_code: null,
        area_code: null,
        dealer_type: null,
        primary_mobile_number: '',
        secondary_mobile_number: '',
        piyestra_company: []
      };
      this.piyestraSettings = {};
      this.markerPosition = null;
      this.locationError = '';
      this.phoneNumberError = '';
      this.showSecondaryPhone = false;
    },

    async downloadTemplate() {
      const templateData = [
        ['dealer name', 'dealer code', 'company name', 'dealer type', 'address', 'region code', 'area code', 'mobile_number', 'mobile_number2', 'company', 'credit limit', 'credit period', 'cash limit', 'cash period', 'lat', 'lng'],
        ['ABC Traders', 'D001', 'ABC Pvt Ltd', 'Furniture', 'Colombo', 'C116-COGA', 'A05MM', '771111111', '', 'Nestle', '20', '30', '61', '15', '7.71111111', '80.2222222'],
        ['Sunrise Distributors', 'D002', 'Sunrise Pvt Ltd', 'Electronic', 'Kandy', 'KU1346', 'KU0063', '773333333', '772222222', '30', 'HIGH', '25', '4', '15', '7.82222222', '80.3333333'],
        ['Green Lanka', 'D003', 'Green Lanka Ltd', 'Hardware', 'Galle', 'RG0012', 'KU0013', '775555555', '', 'Keells', '30', '30', '61', '20', '7.93333333', '80.4444444'],
        ['City Suppliers', 'D004', 'City Pvt Ltd', 'Multi', 'Matara', 'RG005', 'MT001', '777777777', '778888888', 'Nestle', '30', '10', '61', '10', '8.04444444', '80.5555556'],
        ['Metro Dealers', 'D005', 'Metro Pvt Ltd', 'Furniture', 'Negombo', 'RG004', 'CO001', '779999999', '', 'CIC', '18', '20', '61', '15', '8.15555556', '80.6666667']
      ];

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(templateData);

      ws['!cols'] = [
        { wch: 20 }, { wch: 15 }, { wch: 25 }, { wch: 15 }, { wch: 30 }, { wch: 15 }, { wch: 15 },
        { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
        { wch: 15 }, { wch: 15 }, { wch: 15 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Template');
      XLSX.writeFile(wb, 'dealer_bulk_upload_template.xlsx');

      this.snackbarMessage = 'Template downloaded successfully';
      this.snackbarColor = 'success';
      this.snackbar = true;
    },

    homeReload() {
      window.location.href = '/dashboard';
    },

    closeBulkUploadDialog() {
      this.dealerBulkUploadDialog = false;
      this.clearPreview();
    }
  }
};
</script>

<style scoped>
@import "../../styles/outlet_management/create_outlet.scss";

.cursor-pointer {
  cursor: pointer;
}

.flag-icon {
  width: 30px;
  height: 20px;
  object-fit: cover;
}

.custom_input_field_color .v-field {
  background-color: white !important;
}

.custom_centered_overlay {
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom_lat_lon_label {
  background-color: rgba(0, 0, 0, 0.7);
  color: white !important;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  bottom: 10px;
  left: 10px;
  z-index: 1000;
}

.map-error {
  border: 2px solid red;
  border-radius: 4px;
}

.custom_fullscreen_dialog_toolbar {
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: 0 !important;
  padding-right: 16px !important;
}

.custom_company_logo_nav {
  width: 110px;
  height: 35px;
}

.custom_table_normal {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.custom_table_normal .v-data-table-header {
  background-color: #f5f5f5;
}

.v-data-table .v-chip {
  font-size: 12px;
}

.divider-vertical {
  margin: 0 8px;
}

.gray-text {
  color: #666;
}

@media (max-width: 600px) {
  .custom_tool_bar_style {
    flex-wrap: wrap;
  }

  .v-btn {
    margin: 4px 0;
  }
}
</style>